import { CSSProperties } from 'react';

import { themeByService } from '@repo/styles';
import cn from 'classnames';

import { IThrillIconProps, ThrillIconVariant } from '.';

/**
 * 팅글 짜릿(성인) 표식 — 리브랜딩 브랜드 마크 적용본.
 *
 * 글리프는 신규 브랜드 스파클(Figma icon-fill 780:43189, `TingleSparkleMarkIcon`·`TingleRoundLogoIcon`과 동일 아트워크)
 * 하나로 통일한다. 리브랜딩 전에는 variant마다 구 4각 별이 제각각 박혀 있었다.
 * 원본 좌표계는 56 뷰박스라 variant별 뷰박스에 맞춰 transform으로만 배치한다(패스 재작성 없이 정본 유지).
 *
 * 색은 리브랜딩 대상이 아니다 — 짜릿 표식은 성인 의미색(static/adult)을 유지하고 형태만 바꾼다.
 */

/** 신규 브랜드 스파클 원본 패스 (56 뷰박스, bbox x 12.096~43.903 / y 9.830~46.170) */
const SPARKLE_PATH =
  'M27.8149 9.83008C28.4595 9.83009 29.0402 10.2799 29.5464 11.0117C30.0563 11.749 30.5176 12.81 30.9184 14.1191C31.7111 16.7084 32.2762 20.3072 32.4878 24.4014C35.7239 24.6148 38.5329 25.0525 40.5464 25.6377C41.5671 25.9344 42.3935 26.2709 42.9682 26.6416C43.5347 27.007 43.9026 27.436 43.9028 27.9268C43.9028 28.4175 43.5354 28.8465 42.9692 29.2119C42.3948 29.5826 41.5687 29.9201 40.5483 30.2168C38.5361 30.8019 35.7288 31.2385 32.4946 31.4521C32.2903 35.5825 31.7277 39.2174 30.9331 41.834C30.5313 43.157 30.0675 44.2302 29.5552 44.9756C29.0468 45.715 28.4631 46.1699 27.8149 46.1699C27.1661 46.1698 26.5824 45.7136 26.0737 44.9727C25.561 44.2259 25.0968 43.1514 24.6948 41.8262C23.8997 39.2048 23.3363 35.5636 23.1333 31.4268C19.9944 31.2004 17.2792 30.7621 15.3354 30.1826C14.3494 29.8887 13.5522 29.5557 12.9975 29.1904C12.4504 28.8301 12.0962 28.4084 12.0962 27.9268C12.0964 27.4455 12.4552 27.015 13.0073 26.6436C13.5679 26.2665 14.3727 25.9188 15.3647 25.6152C17.3512 25.0074 20.1251 24.5624 23.2944 24.4141C26.4571 24.266 28.3303 24.2665 29.4145 24.3037C29.6337 24.3112 29.8208 24.3214 29.98 24.3311C29.8323 24.2485 29.6678 24.1555 29.4917 24.0586C28.806 23.6815 27.9519 23.2258 27.269 22.9062C26.553 22.5711 25.6113 22.1918 24.8462 21.8955C24.4642 21.7476 24.1268 21.6212 23.8852 21.5312C23.7646 21.4863 23.6677 21.4502 23.6011 21.4258C23.5679 21.4136 23.5423 21.4038 23.5249 21.3975C23.5162 21.3943 23.5088 21.3922 23.5044 21.3906C23.5023 21.3899 23.5006 21.3891 23.4995 21.3887H23.4985L23.3735 21.3428L23.3872 21.2109C23.7295 17.9109 24.3146 15.0884 25.06 13.0859C25.4325 12.0856 25.8487 11.2801 26.3013 10.7217C26.7523 10.1652 27.2597 9.83019 27.8149 9.83008Z';

/** 배지 안 글리프(24 뷰박스 기준 높이 14, 중심 12/12) — 구 4각 별과 같은 자리·크기 */
const BADGE_TRANSFORM = 'translate(1.215 1.214) scale(0.3852)';
/** 아이콘 단독 노출(24 뷰박스, 높이 20) */
const GLYPH_TRANSFORM = 'translate(-3.408 -3.411) scale(0.5504)';
/** 사이드메뉴 알약(13 뷰박스, 높이 9) */
const SIDE_MENU_TRANSFORM = 'translate(0.066 -0.37) scale(0.2477)';

function TingleThrillIcon({
  color = themeByService().color.static.adult.light,
  className,
  size = 24,
  rotate,
  flip,
  variant = ThrillIconVariant.DEFAULT,
  style,
}: IThrillIconProps) {
  const defaultStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 100,
    border: `1.2px solid ${color}`,
    background: '#FFF',
  };

  const mergedStyle = {
    width: size,
    height: size,
    transform: `rotate(${rotate}deg)`,
    ...(variant === ThrillIconVariant.DEFAULT ? defaultStyle : {}),
    ...style,
  };
  return (
    <i className={cn('dds-icon', className, { flip })} style={mergedStyle}>
      {variant === ThrillIconVariant.DEFAULT && (
        <svg
          viewBox="0 0 24 24"
          style={{ width: '100%', height: 'auto' }}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g transform={BADGE_TRANSFORM}>
            <path d={SPARKLE_PATH} fill={color} />
          </g>
        </svg>
      )}
      {variant === ThrillIconVariant.ONLY_ICON && (
        <svg
          viewBox="0 0 24 24"
          style={{ width: '100%', height: 'auto' }}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g transform={GLYPH_TRANSFORM}>
            <path d={SPARKLE_PATH} fill={color || '#fff'} />
          </g>
        </svg>
      )}
      {variant === ThrillIconVariant.SIDE_MENU && (
        <svg viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0.5" width="13" height="13" rx="6.5" fill={color} />
          <g transform={SIDE_MENU_TRANSFORM}>
            <path d={SPARKLE_PATH} fill="white" />
          </g>
        </svg>
      )}
      {variant === ThrillIconVariant.SIDE_MENU_NO_FILL && (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g transform={GLYPH_TRANSFORM}>
            <path d={SPARKLE_PATH} fill={color} />
          </g>
        </svg>
      )}
    </i>
  );
}

export default TingleThrillIcon;
